import React from "react";
import { X, CheckCircle } from "lucide-react";
import { Order } from "./types";

interface ViewAllSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrders: Order[];
  onClearSelection: () => void;
}

const ViewAllSelectionModal: React.FC<ViewAllSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedOrders,
  onClearSelection,
}) => {
  // Group orders by country
  const ordersByCountry = React.useMemo(() => {
    const grouped: Record<string, Order[]> = {};
    selectedOrders.forEach((order) => {
      const country = order.country?.toUpperCase() || "UNKNOWN";
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(order);
    });
    return grouped;
  }, [selectedOrders]);

  // Get stage for an order
  const getOrderStage = (order: Order): string => {
    if (order.trackingId) return "Shipped";
    if (order.selectedPostOffice) return "Post Office Selected";
    return "Without Post Office";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-[#264a3e]" />
            Selected Orders ({selectedOrders.length})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {selectedOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No orders selected</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(ordersByCountry).map(([country, orders]) => (
                <div key={country} className="border border-gray-200 rounded-lg">
                  {/* Country Header */}
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">
                      {country} - {orders.length} Order{orders.length !== 1 ? "s" : ""}
                    </h4>
                    <span className="text-xs text-gray-500">
                      {getOrderStage(orders[0])}
                    </span>
                  </div>

                  {/* Orders List */}
                  <div className="divide-y divide-gray-100">
                    {orders
                      .sort((a, b) => b.id - a.id)
                      .map((order) => (
                        <div key={order.id} className="px-4 py-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                #{order.id} • {order.name || "N/A"}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {order.phone || "-"}
                              </div>
                              <div className="mt-1 text-xs text-gray-600">
                                {order.offername || "N/A"}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-sm text-gray-900">
                                {order.price || 0} {order.currency?.toUpperCase() || "EUR"}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Qty: {order.count || 1}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {order.city || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {selectedOrders.length} order
            {selectedOrders.length !== 1 ? "s" : ""} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClearSelection}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAllSelectionModal;

